import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BillingForm from "../../components/BillingForm";
import Cart from "../../components/Cart";
import CheckboxInput from "../../components/CheckboxInput";
import ClienteEntregaForm from "../../components/ClienteEntregaForm";
import Select from "../../components/Select";
import { validateIdentification } from "../../utils/identificationValidation";
import { buildNotasEntrega } from "../../utils/notasEntrega";
import { applyOrderFixedDiscount } from "../../utils/orderFixedDiscount";
import {
  isUsuarioColaborador,
  resolveDatosClienteEntregaLocal,
} from "../../utils/clienteEntrega";
import Swal from "sweetalert2";

import {
  BODEGAS,
  UNIDAD_MEDIDA_UNIDAD,
  DEFAULT_POSITION_IN_SHIPPING_ORDER,
  ESTADOS_ORDENES,
  METODOS_ENTREGA,
  TIENDAS,
  METODOS_PAGO,
  COMPRA_POR_WHATSAPP,
  COMPRA_EN_TIENDA,
  POS_BODEGA_STORAGE_KEY,
} from "../../utils/constants";
import {
  ensureUsbPrinter,
  getUsbPrintErrorMessage,
  printTicketUsb,
} from "../../utils/usbPrinter";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "../../components/Spinner";
import { clearCart } from "../../slices/appSlice";

import {
  setPrintBill,
  setBilling,
  setBillingDetails,
  clearAfterPurchase,
  clearCustomerData,
} from "../../slices/checkoutSlice";
import useAuthFetch from "../../hooks/useAuthFetch";
import { debounce } from "lodash";

const newBillingMockup = {
  nombreFactura: "",
  tipoIdentificacion: 0,
  identificacion: "",
  correoFactura: "",
  actividadEconomica: "",
  direccionLucida: "",
};

export function CheckoutTiendaPage({ cart, user, userIsLogged }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authFetch = useAuthFetch();

  const printBill = useSelector((state) => state.checkout.printBill);
  const billing = useSelector((state) => state.checkout.billing);
  const billingDetails = useSelector((state) => state.checkout.billingDetails);

  const [orderNotes, setOrderNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const orderSubmitInFlightRef = useRef(false);
  const orderIdempotencyKeyRef = useRef(null);
  const [errors, setErrors] = useState([]);
  const [discountPrice, setDiscountPrice] = useState({});
  const [subTotalWhitDiscountPrice, setSubTotalWhitDiscountPrice] =
    useState(null);

  const [withWhatsapp, setWithWhatsapp] = useState(false);
  const [clienteEntregaNombre, setClienteEntregaNombre] = useState("");
  const [clienteEntregaTelefono, setClienteEntregaTelefono] = useState("");
  const [clienteEntregaCodigoPais, setClienteEntregaCodigoPais] = useState(506);
  const { products } = useSelector((state) => state.app.cart);
  const paymentMethod = useSelector((state) => state.checkout.paymentMethod);

  // Leer la bodega seleccionada en el POS desde localStorage
  const posBodegaId = (() => {
    try {
      const v = localStorage.getItem(POS_BODEGA_STORAGE_KEY);
      return v ? Number(v) : BODEGAS.TICOLITAS_GUACIMA;
    } catch {
      return BODEGAS.TICOLITAS_GUACIMA;
    }
  })();

  useEffect(() => {
    setOrderNotes("");
    if (user && isUsuarioColaborador(user)) {
      dispatch(clearCustomerData());
      dispatch(setBilling(false));
      dispatch(setBillingDetails(newBillingMockup));
    }
  }, [dispatch, user]);

  useEffect(() => {
    let sumDiscount = 0;
    let subTotal = cart.total;

    if (products && discountPrice) {
      if (paymentMethod?.esEnEfectivo) {
        subTotal = cart.totalWithEfectiveDiscount;
      } else {
        subTotal = cart.totalWithAnyDiscount;
      }
    }

    if (discountPrice) {
      for (const product in discountPrice) {
        // Check if the product exists in the cart before calculating
        if (discountPrice[product] && cart.products[product]) {
          const newTotalPrice =
            discountPrice[product].precioConDescuento *
            cart.products[product].quantity;
          const oldTotalPrice =
            cart.products[product].product.precioVenta *
            cart.products[product].quantity;
          sumDiscount += oldTotalPrice - newTotalPrice;
        }
      }
    }

    // Ensure we don't get negative values
    subTotal = Math.max(0, subTotal - sumDiscount);

    setSubTotalWhitDiscountPrice(subTotal);
  }, [
    discountPrice,
    cart.products,
    cart.totalWithEfectiveDiscount,
    cart.totalWithAnyDiscount,
    cart.total,
    paymentMethod,
    products,
  ]);

  // Improve the handleDebounceFn function with error handling
  const handleDebounceFn = async (inputValue, productId) => {
    try {
      if (inputValue && inputValue.length >= 1 && inputValue.length <= 2) {
        const totalPrice = await authFetch(
          `producto-detalles/${productId}/descuento/${Number(inputValue)}`
        );

        if (totalPrice && totalPrice.ok) {
          const data = await totalPrice.json();
          setDiscountPrice((prev) => ({
            ...prev,
            [productId]: data,
          }));
        } else {
          console.error(
            "Error fetching discount price:",
            totalPrice?.statusText
          );
          // Remove this product's discount if the API call fails
          setDiscountPrice((prev) => {
            const newState = { ...prev };
            delete newState[productId];
            return newState;
          });
        }
      } else if (inputValue === "" || inputValue === "0") {
        // Clear this product's discount if the input is cleared
        setDiscountPrice((prev) => {
          const newState = { ...prev };
          delete newState[productId];
          return newState;
        });
      }
    } catch (error) {
      console.error("Error in handleDebounceFn:", error);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceFn = useCallback(debounce(handleDebounceFn, 1000), []);

  const handleChange = (e, productId) => {
    if (Number(e.target.value) < 0 || isNaN(Number(e.target.value))) {
      return;
    }
    debounceFn(e.target.value, productId);
  };

  const generateOrderLines = () => {
    const orderLines = [];
    let lineCounter = 1;
    for (const product in cart.products) {
      const productLine = cart.products[product];
      const margin = productLine.product.margen ? productLine.product.margen / 10500 : 1.2; //Se divide entre 10500 para que si alguien ve el API no entienda el valor
      const taxes = 1.13;
      const unitCost = productLine.product.precioVenta / margin / taxes;
      const unitPriceWithoutTaxes = unitCost * margin;
      const subtotalWhitoutDiscount =
        unitPriceWithoutTaxes * productLine.quantity;
      const productDiscount = productLine?.product?.descuentos
        ? productLine.product.descuentos[0]
        : null;
      const manualDiscountRaw = productLine.manualDiscount;
      const manualDiscount = manualDiscountRaw != null && !isNaN(manualDiscountRaw) && manualDiscountRaw > 0
        ? manualDiscountRaw / 100
        : null;
      let discountPercentage = 0;

      let discount = 0;

      if (manualDiscount) {
        discount =
          ((unitCost * productLine.quantity) * manualDiscount).toFixed(2);
        discountPercentage = manualDiscount;
      } else {
        if (
          productDiscount &&
          productDiscount.activo &&
          new Date(productDiscount.validoDesde) <= new Date() &&
          new Date(productDiscount.validaHasta) >= new Date()
        ) {
          if (
            !productDiscount.soloEnEfectivo ||
            (productDiscount.soloEnEfectivo && paymentMethod.esEnEfectivo)
          ) {
            discount =
              (unitCost *
                productDiscount.porcentaje *
                productLine.quantity).toFixed(2);
            discountPercentage = productDiscount.porcentaje;
          }
        }
      }

      // const subtotalWithDiscount = subtotalWhitoutDiscount - discount;

      orderLines.push({
        linea: lineCounter++,
        idProducto: productLine.product.id,
        cantidad: productLine.quantity,
        consumoDiarioGramos: productLine.dailyConsumption * 100,
        porcentajeImpuesto: taxes,
        costoUnitario: parseFloat((productLine.product.precioVenta / taxes).toFixed(2)),
        subtotal: parseFloat((productLine.product.precioVenta * productLine.quantity).toFixed(2)),
        totalImpuestos: parseFloat(((unitCost * (1 - discountPercentage) * productLine.quantity) * margin * (taxes - 1)).toFixed(2)),
        total: parseFloat((productLine.product.precioVenta * productLine.quantity).toFixed(2)),
        descuento: parseFloat(discount),
        totalConDescuento: parseFloat(((productLine.product.precioVenta * productLine.quantity) * (1 - discountPercentage)).toFixed(2)),
        mascotas: productLine.petName || "",
        unidadMedida: UNIDAD_MEDIDA_UNIDAD,
      });
    }
    return applyOrderFixedDiscount(orderLines, cart.discount);
  };

  const placeOrder = async () => {
    if (loading || orderSubmitInFlightRef.current) {
      return;
    }
    orderSubmitInFlightRef.current = true;
    setLoading(true);
    try {
    const tempErrors = [];

    if (!user || !user.token) {
      tempErrors.push({
        message:
          'No has iniciado sesión. Si no tenés usuario podés crear tu usuario en esta misma pantalla en un solo paso y en segundos en la sección de "Cliente".',
        inputId: "noUser",
      });
      setErrors(tempErrors);
      setLoading(false);
      return;
    }

    if (billing) {
      const fieldsToCheck = {
        nombreFactura: "nombre",
        tipoIdentificacion: "tipo de identificación",
        identificacion: "identificación",
        correoFactura: "correo electrónico",
        direccionLucida: "dirección para factura electrónica",
      };
      for (const field in fieldsToCheck) {
        if (!billingDetails[field]) {
          tempErrors.push({
            message: `Por favor completá el campo ${fieldsToCheck[field]} en la sección de "Facturación"`,
            inputId: field,
          });
        }
      }

      // Validar formato de identificación
      if (billingDetails.identificacion && billingDetails.tipoIdentificacion) {
        const validation = validateIdentification(
          billingDetails.identificacion,
          parseInt(billingDetails.tipoIdentificacion)
        );
        if (!validation.isValid) {
          tempErrors.push({
            message: `Error en identificación: ${validation.message}`,
            inputId: "identificacion",
          });
        }
      }
    }

    if (tempErrors.length > 0) {
      setLoading(false);
      setErrors(tempErrors);
      return;
    }

    let usbDevice = null;
    let printWarning = null;
    if (printBill) {
      try {
        usbDevice = await ensureUsbPrinter({
          vendorId: 4070,
          productId: 33054,
        });
      } catch (error) {
        console.error("[CheckoutTiendaPage] printer access error:", error);
        printWarning = getUsbPrintErrorMessage(error);
      }
    }
    setErrors([]);

    const notasEntrega = buildNotasEntrega(orderNotes);
    const datosEntrega = resolveDatosClienteEntregaLocal(
      clienteEntregaNombre,
      clienteEntregaTelefono,
      clienteEntregaCodigoPais
    );
    const { lines, applied } = generateOrderLines();

    const orderPayload = {
      venta: {
        idUsuario: user.id,
        idEstadoVenta: ESTADOS_ORDENES.FINALIZADA,
        realizada: true,
        totalDeTransporte: 0,
        totalConImpuestos: subTotalWhitDiscountPrice != null
          ? subTotalWhitDiscountPrice
          : cart.total,
        descuentoGlobal: applied,
        totalCancelado: null,
        fechaIngreso: new Date(),
        fechaMaximaPago: new Date(),
        fechaRealPago: new Date(),
        fechaEntregaInicio: new Date(),
        fechaEntregaEstimada: new Date(),
        fechaEntregaFinal: new Date(),
        nombreCompletoEntrega: datosEntrega.nombreCompletoEntrega,
        latitud: null,
        longitud: null,
        urlWaze: null,
        provincia: null,
        canton: null,
        distrito: null,
        direccionExacta: null,
        ...(notasEntrega ? { notasEntrega } : {}),
        telefono: parseInt(datosEntrega.telefono, 10),
        codigoPaisTelefono: datosEntrega.codigoPaisTelefono,
        nombreFactura: billing ? billingDetails.nombreFactura : null,
        tipoIdentificacionFactura: billing
          ? billingDetails.tipoIdentificacion
          : null,
        identificacionFactura: billing ? billingDetails.identificacion : null,
        correoFactura: billing ? billingDetails.correoFactura : null,
        actividadEconomica: billing ? billingDetails.actividadEconomica : null,
        direccionLucida: billing ? billingDetails.direccionLucida : null,
        autorizaContactoWhatsapp: cart.whatsappConfirmation,
        confirmaAutorizaContactoWhatsapp: null,
        aceptaTerminosCondiciones: null,
        facturaImpresa: printBill,
        totalConTransporte: subTotalWhitDiscountPrice != null
          ? subTotalWhitDiscountPrice
          : cart.total,
        idMetodoPago: METODOS_PAGO.TARJETA,
        metodoPago: "Pago en tienda",
        pagoRequiereFirma: subTotalWhitDiscountPrice != null
          ? subTotalWhitDiscountPrice > 30000
            ? true
            : false
          : cart.total > 30000
            ? true
            : false,
        requierePos: true,
        indicacionesPostCompra: "No hay",
        idMetodoEntrega: METODOS_ENTREGA.LOCAL,
        metodoEntrega: "Compra en tienda TiColitas",
        distanciaTiendaMetros: 0,
        duracionDesdeTiendaMinutos: 0,
        montoEfectivo: null,
        idTiendaPickup: posBodegaId,
        idBodega: posBodegaId,
        idTransportista: null,
        posicionEnEntrega: DEFAULT_POSITION_IN_SHIPPING_ORDER,
        idOrigenCompra: withWhatsapp ? COMPRA_POR_WHATSAPP : COMPRA_EN_TIENDA,
      },
      lineas: lines,
    };

    if (billing && !billingDetails.id) {
      orderPayload.nuevaFacturacion = billingDetails;
    }

    if (!orderIdempotencyKeyRef.current) {
      orderIdempotencyKeyRef.current =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }

    const orderCreationFetch = await authFetch(
      "ventas",
      orderPayload,
      "POST",
      {
        headers: { "Idempotency-Key": orderIdempotencyKeyRef.current },
      }
    );

    if (!orderCreationFetch) {
      setErrors([
        {
          message:
            "Error de red al crear tu orden. Verificá tu conexión e intentá de nuevo.",
        },
      ]);
      setLoading(false);
      return;
    }

    if (orderCreationFetch.status === 200) {
      orderIdempotencyKeyRef.current = null;
      const orderCreated = await orderCreationFetch.json();

      if (printBill) {
        const orderForPrint = orderPayload.venta;
        orderForPrint.id = orderCreated.id;
        orderForPrint.lineas = orderPayload.lineas;
        orderForPrint.lineas.forEach((linea) => {
          linea.producto = cart.products[linea.idProducto].product;
        });
        const payload = {
          order: orderForPrint,
          vendorId: 4070,
          productId: 33054,
        };
        setLoading(true);
        if (usbDevice) {
          try {
            await printTicketUsb(payload.order, {
              device: usbDevice,
              vendorId: payload.vendorId,
              productId: payload.productId,
            });
          } catch (error) {
            console.error("[CheckoutTiendaPage] print bill error:", error);
            printWarning = getUsbPrintErrorMessage(error);
          }
        }
      }
      if (printWarning) {
        Swal.fire({
          icon: "warning",
          title: "No se pudo imprimir",
          text: `${printWarning} La orden se guardo y podes imprimir manualmente en el detalle de la orden.`,
        });
      }
      dispatch(clearCart());
      dispatch(clearAfterPurchase());
      setOrderNotes("");
      setClienteEntregaNombre("");
      setClienteEntregaTelefono("");
      setClienteEntregaCodigoPais(506);
      navigate(`/pedidos/${orderCreated.id}?desde=checkout`);
    } else {
      try {
        const error = await orderCreationFetch.json();
        const message = error && error.error && error.error.message;
        if (message) {
          dispatch(
            setErrors([
              {
                message,
              },
            ])
          );
        } else {
          throw new Error();
        }
      } catch (error) {
        setErrors([
          {
            message:
              "Lo sentimos, ha ocurrido un error creando tu orden. Intentálo de nuevo, si el error persiste podés escribirnos para asistirte.",
          },
        ]);
      }
    }
    setLoading(false);
    } finally {
      orderSubmitInFlightRef.current = false;
    }
  };

  return (
    <div className="flex flex-wrap w-full p-content py-8 gap-2">
      {loading && <Spinner color="pink" />}
      <Cart
        cart={cart}
        checkout
        isPickup={false}
        isLocalDelivery={true}
        hideButtons
        additionalStyles="flex w-full"
        freeShipping={null}
        handleChange={handleChange}
        discountPrice={discountPrice}
        subTotalWhitDiscountPrice={subTotalWhitDiscountPrice}
      />
      {cart.productsCount > 0 ? (
        <>
          <div className="flex flex-wrap w-full bg-purple p-8">
            <div className="w-full">
              <p className="text-white text-xl mb-6">Información del pedido</p>
            </div>
            <div className="bg-white p-6 w-full mb-2">
              <div className="w-full mt-4">
                <Select
                  label="Factura electrónica:"
                  labelColor="text-base"
                  buttonClasses="border border-purple"
                  options={[
                    {
                      nombreFactura: "No deseo factura electrónica",
                      id: "noBilling",
                    },
                    {
                      nombreFactura: "+ Ingresar nuevos datos",
                      id: "newBilling",
                    },
                  ].map((b) => {
                    return {
                      value: b.id,
                      label: b.nombreFactura,
                    };
                  })}
                  onChange={(value) => {
                    if (value === "noBilling") {
                      dispatch(setBilling(false));
                      dispatch(setBillingDetails(newBillingMockup));
                      return;
                    }
                    if (value === "newBilling") {
                      dispatch(setBillingDetails(newBillingMockup));
                      dispatch(setBilling(true));
                      return;
                    }
                  }}
                  value={
                    (billingDetails && billingDetails.id) ||
                    (billing ? "newBilling" : "noBilling")
                  }
                />
              </div>
              <div className="w-full mt-4">
                {billing && billingDetails && (
                  <BillingForm
                    billing={billingDetails}
                    onChangeValue={(field, value) => {
                      dispatch(
                        setBillingDetails({
                          ...billingDetails,
                          [field]: value,
                        })
                      );
                    }}
                    onChangeAll={(value) => {
                      dispatch(setBillingDetails(value));
                    }}
                  />
                )}
              </div>
            </div>

            <ClienteEntregaForm
              nombre={clienteEntregaNombre}
              telefono={clienteEntregaTelefono}
              codigoPaisTelefono={clienteEntregaCodigoPais}
              onChangeNombre={setClienteEntregaNombre}
              onChangeTelefono={setClienteEntregaTelefono}
              onChangeCodigoPaisTelefono={(numericValue) => {
                setClienteEntregaCodigoPais(numericValue);
              }}
              labelColor="text-white"
              optional
            />
            <div className="w-full mt-4">
              <label htmlFor="notasAlPedido" className="text-white">
                Notas al pedido:
              </label>
              <textarea
                placeholder="Ej: persona con quien dejar el pedido, contactarte antes por el medio que indiques, medidas de seguridad que debemos respetar."
                id="notasAlPedido"
                name="notasAlPedido"
                className="w-full rounded-3xl mt-2 p-6 outline-none border border-purple resize-none"
                rows={3}
                value={orderNotes}
                onChange={(evt) => {
                  setOrderNotes(evt.target.value);
                }}
              />
            </div>
            <div className="w-full mt-4 flex flex-col gap-3">
              <CheckboxInput
                checked={printBill}
                onCheckChange={() => {
                  dispatch(setPrintBill(!printBill));
                }}
                labelColor="text-white"
                label={"Imprimir tiquete electrónico."}
              />
              <CheckboxInput
                checked={withWhatsapp}
                onCheckChange={() => {
                  dispatch(setWithWhatsapp(!withWhatsapp));
                }}
                labelColor="text-white"
                label={"Compra por Whatsapp"}
              />
            </div>
          </div>
          {errors && errors.length > 0 && (
            <div className="w-full my-4 border border-pink p-8">
              Es necesario verificar lo siguiente:
              {errors.map((e, index) => {
                return (
                  <p key={`error_${index}`} className="text-pink my-2 w-full">
                    {e.message}
                  </p>
                );
              })}
            </div>
          )}
          <Cart
            finalButton
            cart={cart}
            checkout
            checkoutErrors={errors && errors.length > 0}
            isPickup={false}
            isLocalDelivery={true}
            hideButtons
            hideProducts
            additionalStyles="flex w-full"
            placeOrderMethod={placeOrder}
            freeShipping={null}
            handleChange={handleChange}
            discountPrice={discountPrice}
            subTotalWhitDiscountPrice={subTotalWhitDiscountPrice}
            loading={loading}
          />
        </>
      ) : (
        <div className="w-full mt-4">
          <p className="w-full">
            Aún no has agregado nada a tu tacita de compras. Te invitamos a
            visitar nuestra tienda y/o utilizar la barra superior para buscar
            los productos favoritos de tu mascota.
          </p>
          <div className="my-8 text-center">
            <Link to="/tienda">
              <button className="bg-purple text-white h-12 px-12 rounded-full">
                Navegar la tienda
              </button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default CheckoutTiendaPage;
